import { useApiClient } from "@frontend/hooks/use-api"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

interface AnnotationTimeMeResponse {
  day_utc: string
  role: string
  today_seconds: number
  cumulative_seconds: number
  last_heartbeat_at: string | null
}

interface AnnotationTimeHeartbeatResponse {
  day_utc: string
  role: string
  today_seconds: number
  cumulative_seconds: number
  last_heartbeat_at: string
}

interface UseAnnotationTimeTrackerParams {
  caseId?: string
  enabled: boolean
  heartbeatMs?: number
}

export interface AnnotationTimeTrackerState {
  isTrackingActive: boolean
  liveElapsedSeconds: number
  totalSeconds: number
  role: string | null
  lastHeartbeatAt: string | null
}

export function useAnnotationTimeTracker({
  caseId,
  enabled,
  heartbeatMs = 15_000
}: UseAnnotationTimeTrackerParams): AnnotationTimeTrackerState {
  const api = useApiClient()
  const [isTrackingActive, setIsTrackingActive] = useState(false)
  const [cumulativePersistedSeconds, setCumulativePersistedSeconds] = useState(0)
  const [role, setRole] = useState<string | null>(null)
  const [lastHeartbeatAt, setLastHeartbeatAt] = useState<string | null>(null)
  const [nowMs, setNowMs] = useState(() => Date.now())
  const activeStartedAtMsRef = useRef<number | null>(null)
  const pendingMsRef = useRef(0)
  const isSendingRef = useRef(false)
  const enabledRef = useRef(enabled)
  const isTrackingActiveRef = useRef(isTrackingActive)
  const previousCaseIdRef = useRef<string | undefined>(caseId)

  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  useEffect(() => {
    isTrackingActiveRef.current = isTrackingActive
  }, [isTrackingActive])

  useEffect(() => {
    if (previousCaseIdRef.current === caseId) return
    previousCaseIdRef.current = caseId
    activeStartedAtMsRef.current = null
    pendingMsRef.current = 0
    isSendingRef.current = false
    setIsTrackingActive(false)
    setCumulativePersistedSeconds(0)
    setRole(null)
    setLastHeartbeatAt(null)
  }, [caseId])

  const consumeCurrentActiveElapsedMs = useCallback((now: number): number => {
    const start = activeStartedAtMsRef.current
    if (start == null) return 0
    if (now <= start) return 0
    activeStartedAtMsRef.current = now
    return now - start
  }, [])

  const recomputeTrackingActive = useCallback(() => {
    if (!(enabled && caseId)) {
      setIsTrackingActive(false)
      return
    }
    const next = document.visibilityState === "visible" && document.hasFocus()
    setIsTrackingActive(next)
  }, [caseId, enabled])

  useEffect(() => {
    if (!(enabled && caseId)) {
      const now = Date.now()
      pendingMsRef.current += consumeCurrentActiveElapsedMs(now)
      activeStartedAtMsRef.current = null
      pendingMsRef.current = 0
      isSendingRef.current = false
      setIsTrackingActive(false)
      setCumulativePersistedSeconds(0)
      setRole(null)
      setLastHeartbeatAt(null)
      return
    }

    recomputeTrackingActive()

    const onVisibilityChange = () => {
      recomputeTrackingActive()
    }
    const onFocus = () => {
      recomputeTrackingActive()
    }
    const onBlur = () => {
      recomputeTrackingActive()
    }

    document.addEventListener("visibilitychange", onVisibilityChange)
    window.addEventListener("focus", onFocus)
    window.addEventListener("blur", onBlur)

    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange)
      window.removeEventListener("focus", onFocus)
      window.removeEventListener("blur", onBlur)
    }
  }, [caseId, consumeCurrentActiveElapsedMs, enabled, recomputeTrackingActive])

  useEffect(() => {
    const now = Date.now()
    if (isTrackingActive) {
      if (activeStartedAtMsRef.current == null) {
        activeStartedAtMsRef.current = now
      }
      return
    }
    pendingMsRef.current += consumeCurrentActiveElapsedMs(now)
    activeStartedAtMsRef.current = null
  }, [consumeCurrentActiveElapsedMs, isTrackingActive])

  useEffect(() => {
    if (!(enabled && caseId)) return
    let isCancelled = false
    const controller = new AbortController()

    async function bootstrapMe() {
      try {
        const response = (await api(`/cases/${caseId}/annotation-time/me`, {
          signal: controller.signal
        })) as AnnotationTimeMeResponse
        if (isCancelled) return
        setCumulativePersistedSeconds(response.cumulative_seconds)
        setRole(response.role ?? null)
        setLastHeartbeatAt(response.last_heartbeat_at ?? null)
      } catch {
        if (isCancelled) return
        setCumulativePersistedSeconds(0)
        setRole(null)
        setLastHeartbeatAt(null)
      }
    }

    void bootstrapMe()

    return () => {
      isCancelled = true
      controller.abort()
    }
  }, [api, caseId, enabled])

  useEffect(() => {
    if (!(enabled && caseId)) return

    const displayInterval = setInterval(() => {
      setNowMs(Date.now())
    }, 1000)

    return () => clearInterval(displayInterval)
  }, [caseId, enabled])

  useEffect(() => {
    if (!(enabled && caseId)) return

    const heartbeatInterval = setInterval(() => {
      if (!(enabledRef.current && isTrackingActiveRef.current)) return
      if (isSendingRef.current) return

      const now = Date.now()
      pendingMsRef.current += consumeCurrentActiveElapsedMs(now)
      const availableSeconds = Math.floor(pendingMsRef.current / 1000)
      if (availableSeconds < 1) return

      const elapsedSeconds = Math.min(availableSeconds, 300)
      isSendingRef.current = true

      void api(`/cases/${caseId}/annotation-time/heartbeat`, {
        method: "PUT",
        body: JSON.stringify({ elapsed_seconds: elapsedSeconds })
      })
        .then((response) => {
          const body = response as AnnotationTimeHeartbeatResponse
          pendingMsRef.current = Math.max(0, pendingMsRef.current - elapsedSeconds * 1000)
          setCumulativePersistedSeconds(body.cumulative_seconds)
          setRole(body.role ?? null)
          setLastHeartbeatAt(body.last_heartbeat_at ?? null)
        })
        .catch(() => {
          // Keep pending time for retry on next heartbeat.
        })
        .finally(() => {
          isSendingRef.current = false
        })
    }, heartbeatMs)

    return () => clearInterval(heartbeatInterval)
  }, [api, caseId, consumeCurrentActiveElapsedMs, enabled, heartbeatMs])

  const liveElapsedSeconds = useMemo(() => {
    const start = activeStartedAtMsRef.current
    if (!isTrackingActive || start == null) return 0
    if (nowMs <= start) return 0
    return Math.floor((nowMs - start) / 1000)
  }, [isTrackingActive, nowMs])

  const totalSeconds = useMemo(() => {
    const pendingFromActive = (() => {
      const start = activeStartedAtMsRef.current
      if (start == null || nowMs <= start) return 0
      return nowMs - start
    })()
    const pendingSeconds = Math.floor((pendingMsRef.current + pendingFromActive) / 1000)
    return cumulativePersistedSeconds + pendingSeconds
  }, [nowMs, cumulativePersistedSeconds])

  return {
    isTrackingActive,
    liveElapsedSeconds,
    totalSeconds,
    role,
    lastHeartbeatAt
  }
}
